import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
// Import the necessary thunks for fetching data
import { fetchPriceList, fetchProfitbilitySummary, fetchDiscountData, fetchAccountData, saveDiscountData, fetchSolutionData, fetchExportExcel, fetchProfitabilityAnalysis, calculateProfitability, fetchProfitabilityByMode, fetchCockpit, cockpitGetConfigs, applyCockpit } from '../Services/ProfitabilityThunk';
import type { CockpitColumnDef, CockpitPricingContext, CockpitRow, ProfitabilityBreakdownRow, ProfitabilityCaseInfo, ProfitabilityHeaderColumn, ProfitabilityItemSource, ProfitabilityOffer, ProfitabilityViewMode } from '../types';
import type { CustomizableColumn } from 'Components/Common/ColumnCustomizeDialog';
import { PROFITABILITY_BREAKDOWN_PAGE_OR_COMPONENT } from '../Analyse/columnPrefsUtils';
// Import the necessary types for the state
interface ProfitabilityState {
    loading: boolean;
    error: string | null;
    data: {
        // Profitability Analysis (breakdown) page data
        analysis: {
            loading: boolean;
            /** Tab/radio switch fetch — does not wipe offer/caseInfo. */
            modeLoading: boolean;
            /** Last fetch/calculate failure message; cleared on next successful load. */
            error: string | null;
            /** False for access blocks (draft / ineligible) where Retry is not useful. */
            errorRetryable: boolean;
            /** Offer loaded by URL offerId from /v2/offer. */
            offer: ProfitabilityOffer | null;
            caseInfo: ProfitabilityCaseInfo | null;
            /** Active level view (View / L2 / L3 / L2+L3 / L3+L2). */
            viewMode: ProfitabilityViewMode;
            /** Dynamic column descriptors returned by /v2/pricing/profitability. */
            header: ProfitabilityHeaderColumn[];
            rows: ProfitabilityBreakdownRow[];
            /**
             * Snapshot of the latest View (POST) header/rows so switching back
             * to View does not require a re-calculate.
             */
            viewSnapshot: {
                header: ProfitabilityHeaderColumn[];
                rows: ProfitabilityBreakdownRow[];
            } | null;
            /** Last payload sent to /v2/pricing/profitability (set after first successful fetch). */
            payload: any;
            /** Rows + payload snapshot from initial load (Reset restores these). */
            baselineRows: ProfitabilityBreakdownRow[];
            baselinePayload: any;
            /** Payload built from the last successful Calculate (used by Apply). */
            applyPayload: any;
            /** Pending grid edits keyed by row.id — resolved to itemId on Calculate. */
            pendingModifiedItems: Record<string, Record<string, unknown>>;
            /** Whether line items came from the profitability table or offer_order_line_items. */
            itemSource: ProfitabilityItemSource | null;
            /** Serializable column order/visibility prefs (no cellRenderers). */
            columnPrefs: CustomizableColumn[];
            /** True after Analyse bootstrap resolved table-preferences (even if empty). */
            prefsLoaded: boolean;
            /**
             * Apply changes is allowed only after a successful Calculate (action "calc")
             * has refreshed the grid.
             */
            canApplyChanges: boolean;
            exportChecked: boolean;
            /** Reserved for /v2/table-preferences when API is enabled. */
            ucpId: string;
            pageOrComponent: string;
            /** Offer subscription duration (months) from the profitability load metadata. */
            subscriptionDurationMonths: number;
            /** Cockpit tab — GET /v2/pricing/getCockpit */
            cockpit: {
                loading: boolean;
                creating: boolean;
                applying: boolean;
                loaded: boolean;
                error: string | null;
                rows: CockpitRow[];
                columns: CockpitColumnDef[];
                pricingContext: CockpitPricingContext | null;
                hasData: boolean;
            };
        };
        summary: {
            loading: boolean,
            data: any[]
        }
        // Define the structure of priceList data
        priceList: {
            loading: boolean,
            selectedTab: string,
            addPriceListEnabled: boolean, // This is used to control the create price list dialog
            searchKey?: string,
            deleteRow: {},
            editRow: {},
            selectedRow?: any[], // Optional, can be defined more specifically
            data: any[],
            pagination: {
                totalCount: number,
                currentPage: number,
                itemsPerPage: number
            },
            solutionType: string, // Default solution type
            solutionListLoading?: boolean, // Optional, can be defined more specifically
            solutionList: { key: string; value: string }[], // List of solutions, explicitly typed
            discountData: {
                loading: boolean,
                data: any, // Replace 'any' with your actual data type
                editOptions: Record<string, any>,
                searchFields: any[],
                tabSelected?: string; // Selected tab for discount data
                isDiscountSaveDisabled?: boolean; // This is used to control the save button in discount data
                isDiscountDataSaving?: boolean; // This is used to control the loading state in discount data
                editRowData: Record<string, any>; // This is used to control the edit row data in discount data
                newlyAddedRowData: Record<string, any>; // This is used to control the add new row data in discount data
                currentSearch:{
                    field: string;
                    search: string;
                    isLoading: boolean;
                },
                // --------- To be removed in future ------------
                addNewRowData: Record<string, any>; // -- renamed to avoid confusion with editRowData
                formData: Record<string, any> // -- renamed to avoid confusion with discountFormData
            };
            discountFormData: {
                [key: string]: {
                    data: {
                        rows: any[]
                    }
                }
            }; // removed after testing
            accountData: {
                loading: boolean,
                data: { id: string; value: string }[]
            };
            accountMap: { id: string; value: string }[],
            formData?: Record<string, any>,
            exportExcel: {
                loading: boolean; // Optional, can be defined more specifically
                data?: any
            }
        }
    }; // Replace 'any' with your actual data type
}

const initialState: ProfitabilityState = {
    loading: false,
    error: null,
    data: {
        analysis: {
            loading: false,
            modeLoading: false,
            error: null,
            errorRetryable: true,
            offer: null,
            caseInfo: null,
            viewMode: "view",
            header: [],
            rows: [],
            viewSnapshot: null,
            payload: null,
            baselineRows: [],
            baselinePayload: null,
            applyPayload: null,
            pendingModifiedItems: {},
            itemSource: null,
            columnPrefs: [],
            prefsLoaded: false,
            canApplyChanges: false,
            exportChecked: false,
            ucpId: "",
            pageOrComponent: PROFITABILITY_BREAKDOWN_PAGE_OR_COMPONENT,
            subscriptionDurationMonths: 0,
            cockpit: {
                loading: false,
                creating: false,
                applying: false,
                loaded: false,
                error: null,
                rows: [],
                columns: [],
                pricingContext: null,
                hasData: false,
            },
        },
        summary: {
            loading: false,
            data: [],
        },
        priceList: {
            selectedTab: "price_list",
            addPriceListEnabled: false, // This is used to control the create price list dialog
            searchKey: '',
            deleteRow: {},
            editRow: {},
            selectedRow: [],
            loading: false,
            data: [],
            pagination: {
                totalCount: 0,
                currentPage: 1,
                itemsPerPage: 10,
            },
            solutionType: 'ndac', // Default solution type
            solutionListLoading: false, // Optional, can be defined more specifically
            solutionList: [] as { key: string; value: string }[], // List of solutions, explicitly typed
            discountData: {
                loading: false,
                data: [],
                editOptions: {},
                searchFields: [],
                tabSelected: "",
                isDiscountSaveDisabled: false, // This is used to control the save button in discount data
                isDiscountDataSaving: false, // This is used to control the loading state in discount data
                newlyAddedRowData: {}, // This is used to control the add new row data in discount data
                editRowData: {}, // This is used to control the edit row data in discount data
                addNewRowData: {}, // This is used to control the add new row data in discount data
                currentSearch:{
                    field: "",
                    search: "",
                    isLoading: false,
                },
                // --- Removed to avoid confusion with addNewRowData
                
                formData: {}
            },
            accountData: {
                loading: false,
                data: [], // This will be typed as { id: any; value: any }[] by the interface
            },
            accountMap: [],
            formData: {},
            discountFormData: {}, // To be removed after testing
            exportExcel: {
                loading: false,
                data: []
            }
        }
    },
};

/**
 * Fills pricingContext.subscriptionDurationMonths from the profitability load
 * metadata. A value sent by the cockpit API itself takes precedence.
 */
const withSubscriptionDuration = (
    pricingContext: CockpitPricingContext | null | undefined,
    subscriptionDurationMonths: number,
): CockpitPricingContext => {
    if (pricingContext?.subscriptionDurationMonths != null) return pricingContext;
    return { ...(pricingContext || {}), subscriptionDurationMonths };
};

const profitabilitySlice = createSlice({
    name: 'profitability',
    initialState,
    reducers: {
        setTabSelected(state, action) {
            // Update the selected tb
            state.data.priceList.selectedTab = action.payload;
        },
        setCreatePriceList(state, action) {
            // Set the create price list state
            state.data.priceList.addPriceListEnabled = action.payload;
        },
        setPriceListDeleteRow(state, action) {
            // Update price list delete row
            state.data.priceList.deleteRow = action.payload;
        },
        setPriceListEditRow(state, action) {
            // Update price list edit row
            state.data.priceList.editRow = action.payload;
        },
        setPriceListSelectedRow(state, action) {
            // Update price list selected row
            state.data.priceList.selectedRow = action.payload;
        },
        setPriceListSearch(state, action) {
            // Update the search keyword in price list
            state.data.priceList.searchKey = action.payload || '';
        },
        updatePriceListPagination(state, action) {
            const paginationKey = action?.payload?.key as keyof typeof state.data.priceList.pagination;
            state.data.priceList.pagination[paginationKey] = action.payload?.value || 0
            // Check the itemsPerPage has changed
            if (action?.payload?.key === 'itemsPerPage') {
                // If itemsPerPage has changed, reset currentPage to 1
                state.data.priceList.pagination.currentPage = 1;
            }
        },
        setSolutionType(state, action) {
            // Update the solution type
            state.data.priceList.solutionType = action.payload;
        },
        setDiscountTabSelected(state, action) {
            // Update the selected tab for discount data
            state.data.priceList.discountData.tabSelected = action.payload;
        },
        setDiscountSaveStatus(state, action) {
            // Update the isDiscountSaveDisabled state
            state.data.priceList.discountData.isDiscountSaveDisabled = action.payload;
        },
        // Set the newly added row data in discount data
        setNewlyAddedRowData(state, action) {
            // Get key and value from action payload
            const { key, value } = action.payload;
            if(key === 'set'){
                state.data.priceList.discountData.newlyAddedRowData = value || {};
                return;
            }
            if(key === 'reset'){
                state.data.priceList.discountData.newlyAddedRowData = {};
                return;
            }
            // Update the newly added row data
            state.data.priceList.discountData.newlyAddedRowData[key] = value;

        },
        setAddNewRowData(state, action) {
            const { actionType } = action.payload;
            if(actionType && actionType === 'reset'){
                state.data.priceList.discountData.addNewRowData = {};
            }
            else if(actionType && actionType === 'edit'){
                state.data.priceList.discountData.addNewRowData = action?.payload?.value || "";
            }
            else{
                state.data.priceList.discountData.addNewRowData[action?.payload?.key] = action?.payload?.value ?? "";
                // Check all the keys's value in state.data.priceList.discountData.addNewRowData, which are not empty
                const allEmpty = Object.values( state.data.priceList.discountData.addNewRowData ).every((value) => !value);
                if(allEmpty){
                    state.data.priceList.discountData.addNewRowData = {};
                }
            }
        },
        // Set current search
        setCurrentSearch(state, action) {
            const { key, value, isLoading, type = "set" } = action.payload;
            if(type === 'set'){
                state.data.priceList.discountData.currentSearch = {field: key, search: value, isLoading };
            }
            if(type === 'reset'){
                state.data.priceList.discountData.currentSearch = {field: "", search: "", isLoading: false};
            }
        },
        // Update the discount data rows
        updatePricelistDiscountData(state, action) {
            // Update the form data for discount data rows
            const { key, rows } = action.payload;
            state.data.priceList.discountData.data[key]['data']['rows'] = rows;
        },
        setPricelistDiscountData(state, action) {
            // Update the form data for discount data rows
            state.data.priceList.discountData.data = action?.payload || {};
        },
        setEditOptions(state, action) {
            const { field, options } = action.payload;
            state.data.priceList.discountData.editOptions[field] = options;
        },
        /**
         * Persist Analyse column customise prefs in Redux (order / visibility / export).
         * DB persistence goes through AnalysisColumnPrefsService (table-preferences).
         */
        setAnalysisColumnPreferences(state, action) {
            const { columns, exportChecked, ucpId } = action.payload || {};
            if (Array.isArray(columns)) {
                state.data.analysis.columnPrefs = columns;
            }
            if (typeof exportChecked === "boolean") {
                state.data.analysis.exportChecked = exportChecked;
            }
            if (typeof ucpId === "string") {
                state.data.analysis.ucpId = ucpId;
            }
        },
        setAnalysisCanApplyChanges(state, action) {
            state.data.analysis.canApplyChanges = !!action.payload;
        },
        setAnalysisPendingModification(
            state,
            action: {
                payload: {
                    rowKey: string;
                    modifications: Record<string, unknown>;
                };
            },
        ) {
            const { rowKey, modifications } = action.payload;
            if (!rowKey || !modifications) {
                return;
            }
            state.data.analysis.pendingModifiedItems[rowKey] = {
                ...state.data.analysis.pendingModifiedItems[rowKey],
                ...modifications,
            };
        },
        clearAnalysisPendingModifications(state) {
            state.data.analysis.pendingModifiedItems = {};
        },
        resetAnalysisToBaseline(state) {
            state.data.analysis.rows = _.cloneDeep(state.data.analysis.baselineRows);
            state.data.analysis.payload = _.cloneDeep(state.data.analysis.baselinePayload);
            state.data.analysis.applyPayload = null;
            state.data.analysis.pendingModifiedItems = {};
            state.data.analysis.canApplyChanges = false;
        },
        setAnalysisViewMode(state, action) {
            state.data.analysis.viewMode = action.payload as ProfitabilityViewMode;
        },
    },
    extraReducers: (builder) => {
        builder
            // Profitability Analysis (breakdown)
            .addCase(fetchProfitabilityAnalysis.pending, (state) => {
                state.data.analysis.loading = true;
                state.data.analysis.pendingModifiedItems = {};
                state.data.analysis.modeLoading = false;
                state.data.analysis.error = null;
                state.data.analysis.errorRetryable = true;
                // Clear previous offer view so URL/offer changes don't flash stale data.
                state.data.analysis.offer = null;
                state.data.analysis.caseInfo = null;
                state.data.analysis.viewMode = "view";
                state.data.analysis.header = [];
                state.data.analysis.rows = [];
                state.data.analysis.viewSnapshot = null;
                state.data.analysis.baselineRows = [];
                state.data.analysis.baselinePayload = null;
                state.data.analysis.applyPayload = null;
                state.data.analysis.itemSource = null;
                state.data.analysis.columnPrefs = [];
                state.data.analysis.prefsLoaded = false;
                state.data.analysis.canApplyChanges = false;
                state.data.analysis.subscriptionDurationMonths = 0;
                state.data.analysis.cockpit.loading = true;
                state.data.analysis.cockpit.loaded = false;
                state.data.analysis.cockpit.creating = false;
                state.data.analysis.cockpit.error = null;
                state.data.analysis.cockpit.rows = [];
                state.data.analysis.cockpit.columns = [];
                state.data.analysis.cockpit.pricingContext = null;
                state.data.analysis.cockpit.hasData = false;
            })
            .addCase(fetchProfitabilityAnalysis.fulfilled, (state, action) => {
                state.data.analysis.loading = false;
                state.data.analysis.error = null;
                state.data.analysis.errorRetryable = true;
                state.data.analysis.offer = action?.payload?.offer || null;
                state.data.analysis.itemSource = action?.payload?.itemSource || null;
                state.data.analysis.caseInfo = action?.payload?.caseInfo || null;
                state.data.analysis.viewMode = action?.payload?.viewMode || "view";
                const nextHeader = action?.payload?.header || [];
                const nextRows = action?.payload?.rows || [];
                const hasIncomingGrid =
                    nextHeader.length > 0 || nextRows.length > 0;
                const hasExistingGrid =
                    (state.data.analysis.header?.length ?? 0) > 0 ||
                    (state.data.analysis.rows?.length ?? 0) > 0;
                // Late empty load must not wipe grid data written by applyCockpit.
                if (hasIncomingGrid || !hasExistingGrid) {
                    state.data.analysis.header = nextHeader;
                    state.data.analysis.rows = nextRows;
                    state.data.analysis.viewSnapshot = {
                        header: nextHeader,
                        rows: nextRows,
                    };
                    if (action?.payload?.payload) {
                        state.data.analysis.payload = action.payload.payload;
                        state.data.analysis.baselineRows = _.cloneDeep(nextRows);
                        state.data.analysis.baselinePayload = _.cloneDeep(
                            action.payload.payload,
                        );
                        state.data.analysis.applyPayload = null;
                    }
                }
                state.data.analysis.columnPrefs = action?.payload?.columnPrefs || [];
                state.data.analysis.prefsLoaded = !!action?.payload?.prefsLoaded;
                if (typeof action?.payload?.exportChecked === "boolean") {
                    state.data.analysis.exportChecked = action.payload.exportChecked;
                }
                if (typeof action?.payload?.ucpId === "string") {
                    state.data.analysis.ucpId = action.payload.ucpId;
                }
                const previousDuration = state.data.analysis.subscriptionDurationMonths;
                const nextDuration = action?.payload?.subscriptionDurationMonths ?? 0;
                state.data.analysis.subscriptionDurationMonths = nextDuration;
                // Cockpit may have loaded first with the store's value; refresh it,
                // but keep a duration the cockpit API sent itself.
                const cockpitContext = state.data.analysis.cockpit.pricingContext;
                const filledByStore =
                    cockpitContext?.subscriptionDurationMonths == null ||
                    cockpitContext.subscriptionDurationMonths === previousDuration;
                state.data.analysis.cockpit.pricingContext = filledByStore
                    ? { ...(cockpitContext || {}), subscriptionDurationMonths: nextDuration }
                    : cockpitContext;
            })
            .addCase(fetchProfitabilityAnalysis.rejected, (state, action) => {
                state.data.analysis.loading = false;
                const payload = action.payload as
                    | string
                    | { message?: string; retryable?: boolean }
                    | undefined;
                if (payload && typeof payload === "object") {
                    state.data.analysis.error =
                        payload.message ||
                        action.error?.message ||
                        "Failed to fetch profitability analysis";
                    state.data.analysis.errorRetryable = payload.retryable !== false;
                } else {
                    const errorMessage =
                        typeof payload === "string" ? payload : action.error?.message;
                    state.data.analysis.error =
                        errorMessage || "Failed to fetch profitability analysis";
                    state.data.analysis.errorRetryable = true;
                }
                state.error = state.data.analysis.error;
            })
            .addCase(fetchCockpit.pending, (state) => {
                state.data.analysis.cockpit.loading = true;
                state.data.analysis.cockpit.error = null;
            })
            .addCase(fetchCockpit.fulfilled, (state, action) => {
                state.data.analysis.cockpit.loading = false;
                state.data.analysis.cockpit.loaded = true;
                state.data.analysis.cockpit.error = null;
                state.data.analysis.cockpit.rows = action.payload.rows || [];
                if (action.payload.columns?.length) {
                    state.data.analysis.cockpit.columns = action.payload.columns;
                }
                state.data.analysis.cockpit.pricingContext = withSubscriptionDuration(
                    action.payload.pricingContext,
                    state.data.analysis.subscriptionDurationMonths,
                );
                state.data.analysis.cockpit.hasData = !!action.payload.hasData;
            })
            .addCase(fetchCockpit.rejected, (state, action) => {
                state.data.analysis.cockpit.loading = false;
                state.data.analysis.cockpit.loaded = true;
                state.data.analysis.cockpit.creating = false;
                state.data.analysis.cockpit.error =
                    (action.payload as string) ||
                    action.error?.message ||
                    'Failed to fetch cockpit data';
            })
            .addCase(cockpitGetConfigs.pending, (state) => {
                state.data.analysis.cockpit.creating = true;
                state.data.analysis.cockpit.error = null;
            })
            .addCase(cockpitGetConfigs.fulfilled, (state, action) => {
                state.data.analysis.cockpit.creating = false;
                state.data.analysis.cockpit.error = null;
                state.data.analysis.cockpit.rows = action.payload.rows || [];
                state.data.analysis.cockpit.columns =
                    action.payload.columns || [];
                state.data.analysis.cockpit.pricingContext = withSubscriptionDuration(
                    action.payload.pricingContext,
                    state.data.analysis.subscriptionDurationMonths,
                );
                state.data.analysis.cockpit.hasData = !!action.payload.hasData;
            })
            .addCase(cockpitGetConfigs.rejected, (state, action) => {
                state.data.analysis.cockpit.creating = false;
                state.data.analysis.cockpit.error =
                    (action.payload as string) ||
                    action.error?.message ||
                    'Failed to fetch cockpit config';
            })
            .addCase(applyCockpit.pending, (state) => {
                state.data.analysis.cockpit.applying = true;
                state.data.analysis.cockpit.error = null;
                state.data.analysis.loading = true;
            })
            .addCase(applyCockpit.fulfilled, (state, action) => {
                state.data.analysis.cockpit.applying = false;
                state.data.analysis.cockpit.error = null;
                if (action.payload.cockpitRows) {
                    state.data.analysis.cockpit.rows = action.payload.cockpitRows;
                }
                state.data.analysis.loading = false;
                state.data.analysis.error = null;
                state.data.analysis.viewMode = "view";
                state.data.analysis.header = action.payload.header || [];
                state.data.analysis.rows = action.payload.rows || [];
                state.data.analysis.viewSnapshot = {
                    header: action.payload.header || [],
                    rows: action.payload.rows || [],
                };
                if (action.payload.columnPrefs?.length) {
                    state.data.analysis.columnPrefs = action.payload.columnPrefs;
                    state.data.analysis.prefsLoaded = true;
                }
                if (action.payload.payload) {
                    state.data.analysis.payload = action.payload.payload;
                    state.data.analysis.baselineRows = _.cloneDeep(action.payload.rows || []);
                    state.data.analysis.baselinePayload = _.cloneDeep(action.payload.payload);
                    state.data.analysis.applyPayload = null;
                }
                state.data.analysis.canApplyChanges = false;
            })
            .addCase(applyCockpit.rejected, (state, action) => {
                state.data.analysis.cockpit.applying = false;
                state.data.analysis.loading = false;
                state.data.analysis.cockpit.error =
                    (action.payload as string) ||
                    action.error?.message ||
                    'Failed to apply cockpit changes';
            })
            // Level tab / radio mode switch (GET L2/L3/nested, or restore View)
            .addCase(fetchProfitabilityByMode.pending, (state, action) => {
                state.data.analysis.modeLoading = true;
                state.data.analysis.error = null;
                state.data.analysis.viewMode = action.meta.arg.mode;
                // CAD edit / Apply only apply to View mode.
                if (action.meta.arg.mode !== "view") {
                    state.data.analysis.canApplyChanges = false;
                }
            })
            .addCase(fetchProfitabilityByMode.fulfilled, (state, action) => {
                state.data.analysis.modeLoading = false;
                state.data.analysis.error = null;
                state.data.analysis.viewMode = action.payload.mode;
                state.data.analysis.header = action.payload.header || [];
                state.data.analysis.rows = action.payload.rows || [];
                if (action.payload.mode === "view" && !action.payload.fromSnapshot) {
                    state.data.analysis.viewSnapshot = {
                        header: action.payload.header || [],
                        rows: action.payload.rows || [],
                    };
                }
            })
            .addCase(fetchProfitabilityByMode.rejected, (state, action) => {
                state.data.analysis.modeLoading = false;
                state.data.analysis.error =
                    (action.payload as string) ||
                    action.error?.message ||
                    "Failed to fetch profitability level view";
                state.error = state.data.analysis.error;
            })
            // Calculate / Apply (recalculate with modified values)
            .addCase(calculateProfitability.pending, (state, action) => {
                state.data.analysis.loading = true;
                state.data.analysis.error = null;
                const isApply =
                    action.meta.arg?.action === 'Apply' ||
                    action.meta.arg?.action === 'apply';
                state.data.analysis.canApplyChanges = false;
                if (!isApply && state.data.analysis.applyPayload) {
                    state.data.analysis.applyPayload = null;
                }
            })
            .addCase(calculateProfitability.fulfilled, (state, action) => {
                state.data.analysis.loading = false;
                state.data.analysis.error = null;
                state.data.analysis.pendingModifiedItems = {};
                state.data.analysis.viewMode = "view";
                state.data.analysis.header = action?.payload?.header || [];
                state.data.analysis.rows = action?.payload?.rows || [];
                state.data.analysis.viewSnapshot = {
                    header: action?.payload?.header || [],
                    rows: action?.payload?.rows || [],
                };
                if (action?.payload?.payload) {
                    state.data.analysis.payload = action.payload.payload;
                }
                const isCalc = action.meta.arg?.action === 'calc';
                const isApply =
                    action.meta.arg?.action === 'Apply' ||
                    action.meta.arg?.action === 'apply';
                if (isCalc && action?.payload?.applyPayload) {
                    state.data.analysis.applyPayload = action.payload.applyPayload;
                    state.data.analysis.canApplyChanges = true;
                } else if (isApply) {
                    state.data.analysis.baselineRows = _.cloneDeep(action.payload?.rows || []);
                    state.data.analysis.baselinePayload = _.cloneDeep(action.payload?.payload || null);
                    state.data.analysis.applyPayload = null;
                    state.data.analysis.canApplyChanges = false;
                } else {
                    state.data.analysis.canApplyChanges = false;
                }
            })
            .addCase(calculateProfitability.rejected, (state, action) => {
                state.data.analysis.loading = false;
                const isApply =
                    action.meta.arg?.action === 'Apply' ||
                    action.meta.arg?.action === 'apply';
                state.data.analysis.canApplyChanges =
                    isApply && !!state.data.analysis.applyPayload?.data;
                state.data.analysis.error =
                    (action.payload as string) ||
                    action.error?.message ||
                    "Failed to calculate profitability";
                state.error = state.data.analysis.error;
            })
            // Summary
            .addCase(fetchProfitbilitySummary.pending, (state) => {
                state.data.summary.loading = true;
            })
            .addCase(fetchProfitbilitySummary.fulfilled, (state, action) => {
                state.data.summary.data = (action?.payload?.["data"] as []) || [];
                state.data.summary.loading = false;
            })
            .addCase(fetchProfitbilitySummary.rejected, (state) => {
                state.data.summary.loading = false;
            })
            // fetchSolutionData
            .addCase(fetchSolutionData.pending, (state, action) => {
                state.data.priceList.solutionListLoading = true;
            })
            .addCase(fetchSolutionData.fulfilled, (state, action) => {
                // Set the solutionList
                state.data.priceList.solutionListLoading = false;
                state.data.priceList.solutionList = (action?.payload || []);
            })
            .addCase(fetchSolutionData.rejected, (state) => {
                state.data.priceList.solutionListLoading = false;
            })
            // fetchPriceList
            .addCase(fetchPriceList.pending, (state) => {
                state.data.priceList.loading = true;
            })
            .addCase(fetchPriceList.fulfilled, (state, action) => {
                state.data.priceList.data = (action?.payload?.["data"] as []) || [];
                state.data.priceList.pagination.totalCount = action?.payload?.total_count || 0;
                state.data.priceList.loading = false;
                // priceListAccountMap
                state.data.priceList.accountMap = (action?.payload as any)?.["accountMapList"] || [];
            })
            .addCase(fetchPriceList.rejected, (state) => {
                state.data.priceList.loading = false;
            })
            // fetchDiscountData
            .addCase(fetchDiscountData.pending, (state) => {
                state.data.priceList.discountData.loading = true;
            })
            .addCase(fetchDiscountData.fulfilled, (state, action) => {
                state.data.priceList.discountData.loading = false;
                state.data.priceList.discountData.data = action?.payload?.discountData || {};
                state.data.priceList.discountData.editOptions = action?.payload?.editOptions || {};
                state.data.priceList.discountData.searchFields = action?.payload?.searchFields || [];
            })
            .addCase(fetchDiscountData.rejected, (state) => {
                state.data.priceList.discountData.loading = false;
            })
            // fetchAccountData
            .addCase(fetchAccountData.pending, (state) => {
                state.data.priceList.accountData.loading = true;
            })
            .addCase(fetchAccountData.fulfilled, (state, action) => {
                state.data.priceList.accountData.loading = false;
                // Set Account Data
                const accountData = action?.payload?.["data"].map((item: any) => ({
                    id: item.accountnumber,
                    value: item.accountnumber + ` (${item.name})`,
                    chipLabel: item.accountnumber + ` (${item.name})`,
                }));
                state.data.priceList.accountData.data = accountData || [];
                // Set Account Map - Clean merge to prevent stale chipLabel data
                const existingIds = new Set(accountData.map(a => a.id));
                const filteredExistingMap = state.data.priceList.accountMap
                    .filter(account => !existingIds.has(account.id))
                    .map(account => ({
                        id: account.id,
                        value: account.value,
                        chipLabel: account.value // Ensure chipLabel matches value for existing items
                    }));
                state.data.priceList.accountMap = [...filteredExistingMap, ...accountData];
            })
            .addCase(fetchAccountData.rejected, (state) => {
                state.data.priceList.accountData.loading = false;
            })
            // To handle the dialog close
            .addCase(saveDiscountData.pending, (state) => {
                state.data.priceList.discountData.isDiscountDataSaving = true;
            })
            // To handle the dialog close
            .addCase(saveDiscountData.fulfilled, (state) => {
                state.data.priceList.discountData.isDiscountDataSaving = false;
                state.data.priceList.addPriceListEnabled = false;
                state.data.priceList.editRow = {};
            })
            .addCase(saveDiscountData.rejected, (state) => {
                state.data.priceList.discountData.isDiscountDataSaving = false;
            })
            .addCase(fetchExportExcel.pending, (state) => {
                state.data.priceList.exportExcel.loading = true;
            })
            .addCase(fetchExportExcel.fulfilled, (state, action) => {
                state.data.priceList.exportExcel.loading = false;
                state.data.priceList.exportExcel.data = action.payload?.data?.discountData || {};
            })
            .addCase(fetchExportExcel.rejected, (state) => {
                state.data.priceList.exportExcel.loading = false;
                // Optionally set an error state
                state.error = "Failed to fetch export excel data";
                state.data.priceList.exportExcel.data = [];
            })
    },
});

export const {
    setTabSelected,
    setCreatePriceList,
    setPriceListSearch,
    setPriceListDeleteRow,
    setPriceListEditRow,
    setPriceListSelectedRow,
    updatePriceListPagination,
    setSolutionType,
    setDiscountTabSelected,
    setDiscountSaveStatus,
    setNewlyAddedRowData,
    setAddNewRowData,
    setPricelistDiscountData,
    updatePricelistDiscountData,
    setEditOptions,
    setCurrentSearch,
    setAnalysisColumnPreferences,
    setAnalysisCanApplyChanges,
    setAnalysisPendingModification,
    clearAnalysisPendingModifications,
    resetAnalysisToBaseline,
    setAnalysisViewMode,
} = profitabilitySlice.actions;

export default profitabilitySlice.reducer;